import { useCallback, useRef, useState, type MouseEvent } from "react";
import { FilterList, LooksOneOutlined } from "@mui/icons-material";
import Filter2Icon from "@mui/icons-material/Filter2";
import Filter3Icon from "@mui/icons-material/Filter3";
import Filter4Icon from "@mui/icons-material/Filter4";
import Filter5Icon from "@mui/icons-material/Filter5";
import Filter6Icon from "@mui/icons-material/Filter6";
import Filter7Icon from "@mui/icons-material/Filter7";
import Filter8Icon from "@mui/icons-material/Filter8";
import Filter9Icon from "@mui/icons-material/Filter9";
import Filter9PlusIcon from "@mui/icons-material/Filter9Plus";
import { Popover } from "@mui/material";
import type { OverridableComponent } from "@mui/material/OverridableComponent";
import type { SvgIconTypeMap } from "@mui/material/SvgIcon";
import FiltersPanel from "@components/TableFilters/FiltersPanel/FiltersPanel";
import { type FilterItem } from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { StyledFilterButton } from "@components/TableFilters/TableFilters.styles";
import type { TableFilterProps } from "@components/TableFilters/TableFilters.types";
import { useTranslation } from "react-i18next";

type IconComponent = OverridableComponent<SvgIconTypeMap<{}, "svg">>;

const filterCounterIcons: Record<number, IconComponent> = {
  1: LooksOneOutlined,
  2: Filter2Icon,
  3: Filter3Icon,
  4: Filter4Icon,
  5: Filter5Icon,
  6: Filter6Icon,
  7: Filter7Icon,
  8: Filter8Icon,
  9: Filter9Icon,
};

const TableFilter = ({
  filters,
  onApplyFilters,
  onClearFilters,
  availableFields,
}: TableFilterProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const popoverActionRef = useRef<{ updatePosition: () => void } | null>(null);

  const updatePopoverPosition = useCallback(() => {
    popoverActionRef.current?.updatePosition();
  }, []);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = (filters: FilterItem[]) => {
    onApplyFilters(filters);
    handleClose();
  };

  const getFilterCounter = (count: number) => {
    if (!count) return null;

    if (count > 0 && count < 10) {
      const Icon = filterCounterIcons[count];
      return <Icon />;
    }

    return <Filter9PlusIcon />;
  };

  return (
    <>
      <StyledFilterButton
        testId="table-filters-button"
        variant="contained"
        startIcon={<FilterList />}
        onClick={handleOpen}
        label={t("common.filters")}
        endIcon={getFilterCounter(filters?.length)}
      />

      <Popover
        action={popoverActionRef}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ minWidth: 560, overflow: "visible" }}
      >
        <FiltersPanel
          filters={filters}
          onApplyFilters={handleApply}
          onCancel={handleClose}
          onClearFilters={() => {
            onClearFilters?.();
            updatePopoverPosition();
          }}
          availableFields={availableFields}
          onLayoutChange={updatePopoverPosition}
        />
      </Popover>
    </>
  );
};

export default TableFilter;
